// ── useMentions ───────────────────────────────────────────────────────────────
// The @mention half of a compose surface: who you can mention, detecting a
// half-typed "@han" left of the caret, and inserting a completed handle without
// losing the cursor.
//
// Lifted verbatim out of ReviewComposer so the episode-comment composer can share
// it rather than growing a second, subtly-different implementation — the drift
// that the component standard exists to prevent. Extracted as a HOOK, not a
// component: the review composer keeps its own textarea markup, so adopting this
// is a small diff on a shipped feature instead of a rewrite of one.
//
// The mention pool is deliberately PEOPLE YOU FOLLOW ONLY. The server gates the
// resulting notification to follows as well, so a mention stays a social gesture
// rather than a spam vector.
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
// Caret maths lives in lib/mentionText so it's unit-tested — see mentionText.test.js.
import { detectMention, applyMention, applyAtInsert } from '../lib/mentionText'

export function useMentions({ userId, value, setValue, textareaRef }) {
  const [follows, setFollows] = useState([])
  const [mention, setMention] = useState(null)   // { query, start } while typing @…

  useEffect(() => {
    if (!userId) return
    let alive = true
    supabase.from('follows').select('following_id').eq('follower_id', userId).limit(500)
      .then(async ({ data }) => {
        const ids = (data || []).map(f => f.following_id)
        if (!ids.length || !alive) return
        const { data: profs } = await supabase.from('public_profiles')
          .select('id, username, display_name, avatar_url').in('id', ids)
        if (alive) setFollows((profs || []).filter(p => p.username))
      }, () => {})
    return () => { alive = false }
  }, [userId])

  const detect = useCallback((val, caret) => setMention(detectMention(val, caret)), [])

  const onChange = useCallback((e) => { setValue(e.target.value); detect(e.target.value, e.target.selectionStart) }, [setValue, detect])
  const onCaret  = useCallback((e) => detect(e.target.value, e.target.selectionStart), [detect])

  const insertMention = useCallback((u) => {
    const ta = textareaRef?.current
    const caret = ta ? ta.selectionStart : value.length
    const { next, pos } = applyMention(value, caret, mention, u.username)
    setValue(next)
    setMention(null)
    // Restore the caret AFTER React has painted the new value, or the browser puts
    // it back at the end of the textarea and typing continues in the wrong place.
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = pos } })
  }, [mention, value, setValue, textareaRef])

  // The @ button: drop an @ at the caret (space-prefixed so it starts a word) and
  // open the autocomplete straight away.
  const insertAt = useCallback(() => {
    const ta = textareaRef?.current
    const caret = ta ? ta.selectionStart : value.length
    const { next, pos } = applyAtInsert(value, caret)
    setValue(next)
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = pos; detect(next, pos) } })
  }, [value, setValue, textareaRef, detect])

  const suggestions = mention
    ? follows.filter(f => {
      const q = mention.query
      if (!q) return true
      return (f.username || '').toLowerCase().startsWith(q) ||
             (f.display_name || '').toLowerCase().includes(q)
    }).slice(0, 6)
    : []

  return { follows, mention, setMention, suggestions, onChange, onCaret, insertMention, insertAt }
}
